import React, { useState } from 'react';

const FenceCalculator = () => {
    // State for all input variables
    const [fenceStyle, setFenceStyle] = useState('privacy');
    const [fenceLength, setFenceLength] = useState('');
    const [fenceMaterial, setFenceMaterial] = useState('wood');
    const [fenceHeight, setFenceHeight] = useState('6');
    const [smallGates, setSmallGates] = useState('0');
    const [largeGates, setLargeGates] = useState('0');

    // Cost constants
    const materialCosts = {
        wood: 12.50, // $ per linear ft for 6ft height (privacy/picket)
        vinyl: 15.00, // Scaled relatively
        aluminum: 17.50,
        chainlink: 5.00,
    };

    const heightMultipliers = {
        '4': 0.8,
        '6': 1,
        '8': 1.3,
    };

    const styleCosts = {
        '3panel': 6, // Scaled from $12 when wood was $25
        '4panel': 7, // Scaled from $14 when wood was $25
    };

    const costs = {
        smallGate: 200, // Fixed price per small gate
        largeGate: 500, // Fixed price per large gate
        laborRate: 20, // $ per linear ft
        markupRate: 0.2, // 20% markup on material + labor
    };

    // Calculate total cost based on fence style
    const calculateCost = () => {
        const length = parseFloat(fenceLength) || 0;
        const smallGatesNum = parseInt(smallGates) || 0;
        const largeGatesNum = parseInt(largeGates) || 0;

        let materialCost = 0;
        let laborCost = 0;

        // Calculate costs based on fence style
        if (fenceStyle === 'privacy' || fenceStyle === 'picket') {
            const baseMaterialCost = materialCosts[fenceMaterial] || 12.50;
            const heightMultiplier = heightMultipliers[fenceHeight] || 1;
            materialCost = length * baseMaterialCost * heightMultiplier;
            laborCost = length * costs.laborRate;
        } else if (fenceStyle === '3panel' || fenceStyle === '4panel') {
            materialCost = length * styleCosts[fenceStyle];
            laborCost = length * costs.laborRate;
        } else if (fenceStyle === 'ranch') {
            const baseMaterialCost = materialCosts.wood; // Ranch is wood only
            const heightMultiplier = heightMultipliers[fenceHeight] || 1;
            materialCost = length * baseMaterialCost * heightMultiplier;
            laborCost = length * costs.laborRate;
        }

        // Gate costs
        const smallGatesCost = smallGatesNum * costs.smallGate;
        const largeGatesCost = largeGatesNum * costs.largeGate;

        // Profit (markup on material + labor)
        const profit = (materialCost + laborCost) * costs.markupRate;

        // Total
        const totalCost = materialCost + laborCost + smallGatesCost + largeGatesCost + profit;

        return {
            materialCost: materialCost.toFixed(2),
            laborCost: laborCost.toFixed(2),
            smallGatesCost: smallGatesCost.toFixed(2),
            largeGatesCost: largeGatesCost.toFixed(2),
            profit: profit.toFixed(2),
            totalCost: totalCost.toFixed(2),
        };
    };

    const costsBreakdown = calculateCost();

    return (
        <div className="max-w-xl mx-auto p-6 font-sans">
            <h2 className="text-2xl font-bold mb-6 text-center">Fencing Job Calculator</h2>
            <div className="space-y-6">
                {/* Fence Style Selector */}
                <div>
                    <label className="block text-sm font-medium mb-2">
                        Fence Style:
                        <select
                            value={fenceStyle}
                            onChange={(e) => setFenceStyle(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="privacy">Privacy Fence</option>
                            <option value="3panel">3 Panel Wood</option>
                            <option value="4panel">4 Panel Wood</option>
                            <option value="picket">Picket Fence</option>
                            <option value="ranch">Ranch Rail</option>
                        </select>
                    </label>
                </div>

                {/* Fence Length (all styles) */}
                <div>
                    <label className="block text-sm font-medium mb-2">
                        Fence Length (linear ft):
                        <input
                            type="number"
                            min="0"
                            value={fenceLength}
                            onChange={(e) => setFenceLength(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </label>
                </div>

                {/* Material (privacy and picket only) */}
                {(fenceStyle === 'privacy' || fenceStyle === 'picket') && (
                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Fence Material:
                            <select
                                value={fenceMaterial}
                                onChange={(e) => setFenceMaterial(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="wood">Wood ($12.50/ft)</option>
                                <option value="vinyl">Vinyl ($15.00/ft)</option>
                                <option value="aluminum">Aluminum ($17.50/ft)</option>
                                <option value="chainlink">Chainlink ($5.00/ft)</option>
                            </select>
                        </label>
                    </div>
                )}

                {/* Height (privacy, picket, ranch) */}
                {(fenceStyle === 'privacy' || fenceStyle === 'picket' || fenceStyle === 'ranch') && (
                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Fence Height (ft):
                            <select
                                value={fenceHeight}
                                onChange={(e) => setFenceHeight(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="4">4 ft</option>
                                <option value="6">6 ft</option>
                                <option value="8">8 ft</option>
                            </select>
                        </label>
                    </div>
                )}

                {/* Small Gates (all styles) */}
                <div>
                    <label className="block text-sm font-medium mb-2">
                        Number of Small Gates ($200 each):
                        <input
                            type="number"
                            min="0"
                            value={smallGates}
                            onChange={(e) => setSmallGates(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </label>
                </div>

                {/* Large Gates (all styles) */}
                <div>
                    <label className="block text-sm font-medium mb-2">
                        Number of Large Gates ($500 each):
                        <input
                            type="number"
                            min="0"
                            value={largeGates}
                            onChange={(e) => setLargeGates(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </label>
                </div>

                {/* Cost Breakdown */}
                <div className="mt-6 p-4 bg-gray-50 rounded-md">
                    <h3 className="text-lg font-semibold mb-4">Cost Breakdown</h3>
                    <p className="mb-2">Material Cost: ${costsBreakdown.materialCost}</p>
                    <p className="mb-2">Labor Cost: ${costsBreakdown.laborCost}</p>
                    <p className="mb-2">Small Gates Cost: ${costsBreakdown.smallGatesCost}</p>
                    <p className="mb-2">Large Gates Cost: ${costsBreakdown.largeGatesCost}</p>
                    <p className="mb-2">Profit: ${costsBreakdown.profit}</p>
                    <h4 className="text-xl font-bold mt-4">Total Cost: ${costsBreakdown.totalCost}</h4>
                </div>
            </div>
        </div>
    );
};

export default FenceCalculator;